import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { AxiosResponse } from "axios";
import { ref, type Ref } from "vue";

interface IntegrationStatus {
  last_saved_at: string | null;
  saved_by: string;
  online?: boolean;
}

export function useRegistryStatus() {
  const { globalErrorHandler } = useGlobalHandlers();
  const integrationStatus = ref<Record<string, IntegrationStatus>>({});
  const isCheckingStatus = ref<Record<string, boolean>>({});
  const isTestingAuth = ref<Record<string, boolean>>({});
  const isTestingCompanies = ref<Record<string, boolean>>({});
  const isTestingDevices = ref<Record<string, boolean>>({});
  const isTestingCertificates = ref<Record<string, boolean>>({});
  const isTestingIssuanceHistory = ref<Record<string, boolean>>({});
  const isTestingTransfer = ref<Record<string, boolean>>({});
  const isSyncingCompanies = ref<Record<string, boolean>>({});

  const fetchRegistriesData = async () => {
    try {
      const response = await goSolidApi.get("/api/admin/registry/integrations");
      if (response.status === 200) {
        integrationStatus.value = response.data;
      }
    } catch (error) {
      globalErrorHandler(error);
    }
  };

  const checkRegistryStatus = async (integrationName?: string) => {
    if (integrationName) {
      return await checkSingleRegistryStatus(integrationName);
    } else {
      return await checkAllRegistriesStatus();
    }
  };

  const checkSingleRegistryStatus = async (integrationName: string) => {
    isCheckingStatus.value[integrationName] = true;
    try {
      const response = await goSolidApi.get(
        `/api/admin/registry/status?integration=${integrationName}`
      );
      if (response.status === 200) {
        if (!integrationStatus.value[integrationName]) {
          integrationStatus.value[integrationName] = {
            last_saved_at: null,
            saved_by: "",
            online: response.data.online,
          };
        } else {
          integrationStatus.value[integrationName].online =
            response.data.online;
        }
      }
    } catch (error) {
      globalErrorHandler(error);
    } finally {
      isCheckingStatus.value[integrationName] = false;
    }
  };

  const checkAllRegistriesStatus = async () => {
    Object.keys(integrationStatus.value).forEach((integrationName) => {
      isCheckingStatus.value[integrationName] = true;
    });
    try {
      const response = await goSolidApi.get("/api/admin/registry/status");
      if (response.status === 200) {
        for (const [integrationName, data] of Object.entries(response.data)) {
          if (!integrationStatus.value[integrationName]) {
            integrationStatus.value[integrationName] = {
              last_saved_at: null,
              saved_by: "",
              online: (data as any).online,
            };
          } else {
            integrationStatus.value[integrationName].online = (
              data as any
            ).online;
          }
        }
      }
    } catch (error) {
      globalErrorHandler(error);
    } finally {
      Object.keys(integrationStatus.value).forEach((integrationName) => {
        isCheckingStatus.value[integrationName] = false;
      });
    }
  };

  const runFlagged = async (
    flags: Ref<Record<string, boolean>>,
    integrationName: string,
    request: () => Promise<AxiosResponse>
  ) => {
    flags.value[integrationName] = true;
    try {
      const response = await request();
      return response.data;
    } catch (error) {
      globalErrorHandler(error);
      throw error;
    } finally {
      flags.value[integrationName] = false;
    }
  };

  const testRegistryAuth = (integrationName: string) =>
    runFlagged(isTestingAuth, integrationName, () =>
      goSolidApi.post("/api/admin/registry/run-auth", {
        integration: integrationName,
      })
    );

  const testRegistryCompanies = (integrationName: string) =>
    runFlagged(isTestingCompanies, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-companies", {
        integration: integrationName,
      })
    );

  const testRegistryDevices = (
    integrationName: string,
    registryCompanyId: string
  ) =>
    runFlagged(isTestingDevices, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-devices", {
        integration: integrationName,
        registry_company_id: registryCompanyId,
      })
    );

  const testRegistryCertificates = (
    integrationName: string,
    registryCompanyId: string
  ) =>
    runFlagged(isTestingCertificates, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-certificates", {
        integration: integrationName,
        registry_company_id: registryCompanyId,
      })
    );

  const testRegistryIssuanceHistory = (
    integrationName: string,
    registryCompanyId: string
  ) =>
    runFlagged(isTestingIssuanceHistory, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-issuance-history", {
        integration: integrationName,
        registry_company_id: registryCompanyId,
      })
    );

  const testRegistryTransfer = (
    integrationName: string,
    certificateId: string,
    fromAccount: string,
    toAccount: string
  ) =>
    runFlagged(isTestingTransfer, integrationName, () =>
      goSolidApi.post("/api/admin/registry/test-transfer", {
        integration: integrationName,
        certificate_id: certificateId,
        from_account: fromAccount,
        to_account: toAccount,
      })
    );

  const syncCompaniesFromRegistry = (integrationName: string) =>
    runFlagged(isSyncingCompanies, integrationName, () =>
      goSolidApi.post(
        `/api/admin/registry/integrations/${integrationName}/sync-companies`
      )
    );

  return {
    integrationStatus,
    isCheckingStatus,
    isTestingAuth,
    isTestingCompanies,
    isTestingDevices,
    isTestingCertificates,
    isTestingIssuanceHistory,
    isTestingTransfer,
    isSyncingCompanies,
    fetchRegistriesData,
    checkRegistryStatus,
    testRegistryAuth,
    testRegistryCompanies,
    testRegistryDevices,
    testRegistryCertificates,
    testRegistryIssuanceHistory,
    testRegistryTransfer,
    syncCompaniesFromRegistry,
  };
}
